import { API_BASE_URL } from '../config/runtime';

const DB_NAME = 'amposter-home-feed-v1';
const STORE = 'accounts';
const ACCOUNT_KEY = `amposter:home-feed-account:v1:${API_BASE_URL}`;
const keyFor = (accountId) => `${API_BASE_URL}:${accountId}`;

async function transaction(mode, action) {
  const db = await new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error('IndexedDB unavailable')); return; }
    const request = window.indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export const readFeedCache = (id) => transaction('readonly', (store) => store.get(keyFor(id))).catch(() => null);
export const writeFeedCache = (id, feeds) => transaction('readwrite', (store) => store.put({ version: 1, feeds }, keyFor(id))).catch(() => undefined);
export const deleteFeedCache = (id) => transaction('readwrite', (store) => store.delete(keyFor(id))).catch(() => undefined);

// Shared page snapshots use the same database, with a separate key namespace.
const pagesKeyFor = (id) => `${API_BASE_URL}:pages:${id}`;
export const readPageCache = (id) => transaction('readonly', (store) => store.get(pagesKeyFor(id))).catch(() => null);
export const writePageCache = (id, entries) => transaction('readwrite', (store) => store.put({ version: 1, entries }, pagesKeyFor(id))).catch(() => undefined);
export const deletePageCache = (id) => transaction('readwrite', (store) => store.delete(pagesKeyFor(id))).catch(() => undefined);

// Only an account identifier, never credentials or cached permissions.
export function rememberFeedAccount(id) {
  try { localStorage.setItem(ACCOUNT_KEY, String(id)); } catch (_) { /* optional persistence */ }
}
export function readFeedAccount() {
  try { return localStorage.getItem(ACCOUNT_KEY); } catch (_) { return null; }
}
export function forgetFeedAccount() {
  try { localStorage.removeItem(ACCOUNT_KEY); } catch (_) { /* optional persistence */ }
}

const isLegacyFeedKey = (key) => /^(?:amposter_v.+_)?amposter_posts_(cache|timestamp)$/.test(key)
  || /^(public_posts|latest_posts_\d+|typefeed_.+)$/.test(key);
const isRetiredPageKey = (key) => isLegacyFeedKey(key) || key.startsWith('homePosts_scroll_')
  || /^amposter_cache_talent_catalog_backend_v\d+$/.test(key)
  || /^characters_cache_(prophets|companions|historical)$/.test(key)
  || /^(teamPostsCache:|teamPostsApiCache:v\d+:)/.test(key);
let legacyCleanup;

function cleanLegacyDatabase(name, storeName) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => { settled = true; resolve(); };
    try {
      // Opening without a version works even without indexedDB.databases().
      // Abort creation if this user never had the old database.
      const open = window.indexedDB.open(name);
      open.onupgradeneeded = () => open.transaction.abort();
      open.onerror = open.onblocked = finish;
      open.onsuccess = () => {
        const db = open.result;
        if (settled || !db.objectStoreNames.contains(storeName)) { db.close(); finish(); return; }
        try {
          const tx = db.transaction(storeName, 'readwrite');
          tx.oncomplete = tx.onerror = tx.onabort = () => { db.close(); finish(); };
          const cursor = tx.objectStore(storeName).openCursor();
          cursor.onsuccess = () => {
            const entry = cursor.result;
            if (!entry) return;
            if (isRetiredPageKey(String(entry.key))) entry.delete();
            entry.continue();
          };
        } catch (_) { db.close(); finish(); }
      };
    } catch (_) { finish(); }
  });
}

export function removeLegacyPageCaches() {
  // Runs on every app launch, including the login screen. No permanent "done"
  // flag: retry failed cleanup and remove keys recreated by an older open tab.
  // Delete only superseded read caches. Offline edits, drafts, downloaded Quran
  // content, media and user preferences remain owned by their existing features.
  for (const storageName of ['localStorage', 'sessionStorage']) {
    try {
      const storage = window[storageName];
      Object.keys(storage).filter(isRetiredPageKey)
        .forEach((key) => storage.removeItem(key));
    } catch (_) { /* one unavailable storage must not block the others */ }
  }
  if (!legacyCleanup) {
    legacyCleanup = Promise.all([
      cleanLegacyDatabase('AmposterDB', 'posts'),
      cleanLegacyDatabase('AmposterCacheDB', 'keyvalStore'),
    ]).finally(() => { legacyCleanup = null; });
  }
  return legacyCleanup;
}

export const removeLegacyHomeFeedCache = removeLegacyPageCaches;
